// Shared AudioContext + master/sfx/music gain nodes for the WhatIs browser
// source. The single context is created lazily so SSR/jsdom tests don't crash;
// `unlock()` calls `resume()` (the no-op-but-required incantation that lifts
// the autoplay block) and is safe to call from any user gesture or from
// network events that strongly imply the page is foregrounded.

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let sfxGainNode: GainNode | null = null;
let musicGainNode: GainNode | null = null;
let unlocked = false;
const listeners = new Set<(unlocked: boolean) => void>();

function ensureContext(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof window === 'undefined') return null;
  const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);
    sfxGainNode = ctx.createGain();
    sfxGainNode.gain.value = 1;
    sfxGainNode.connect(master);
    musicGainNode = ctx.createGain();
    musicGainNode.gain.value = 0.4;
    musicGainNode.connect(master);
    if (ctx.state === 'running') {
      unlocked = true;
    }
  } catch (err) {
    console.warn('[audio-bus] AudioContext init failed:', err);
    ctx = null;
  }
  return ctx;
}

function notify(): void {
  for (const fn of listeners) fn(unlocked);
}

export const audioBus = {
  context(): AudioContext | null {
    return ensureContext();
  },
  sfxGain(): GainNode {
    ensureContext();
    return sfxGainNode!;
  },
  musicGain(): GainNode {
    ensureContext();
    return musicGainNode!;
  },
  isUnlocked(): boolean {
    return unlocked;
  },
  async unlock(): Promise<boolean> {
    const c = ensureContext();
    if (!c) return false;
    if (c.state === 'running') {
      if (!unlocked) {
        unlocked = true;
        notify();
      }
      return true;
    }
    try {
      await c.resume();
      // After await, c.state can be any AudioContextState — re-read fresh.
      const stateAfter: string = c.state;
      const ok = stateAfter === 'running';
      if (ok && !unlocked) {
        unlocked = true;
        notify();
      }
      return ok;
    } catch (err) {
      console.debug('[audio-bus] resume blocked:', err);
      return false;
    }
  },
  onChange(cb: (unlocked: boolean) => void): () => void {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },
  setMasterVolume(v: number): void {
    ensureContext();
    if (master) master.gain.value = Math.max(0, Math.min(1, v));
  },
  setMusicVolume(v: number): void {
    ensureContext();
    if (musicGainNode) musicGainNode.gain.value = Math.max(0, Math.min(1, v));
  },
};
